import { useQuery } from '@apollo/client';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@synoptic/ui-kit/collapsible/collapsible.js';
import { MinusIcon } from '@synoptic/ui-kit/icons/react/minus.js';
import { PlusIcon } from '@synoptic/ui-kit/icons/react/plus.js';
import { Spinner } from '@synoptic/ui-kit/spinner/spinner.js';
import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { gql } from '@/__generated__';
import { Stream } from '@/__generated__/schema-types';
import { TargetsModal } from '@/components/streams/custom-stream/targets-modal';
import { useSubsetLists } from '@/components/streams/custom-stream/use-subset-lists';
import {
  ONBOARDING_TARGETS,
  onboardingTargetProps,
} from '@/routes/_app.terminal._index/terminal/onboarding/onboarding-targets';
import { useMe } from '@/routes/_app/use-me';
import {
  isSubscribedCustomStream,
  SourceCustomTargetsMap,
} from './source-custom-targets';
import { StreamTargetsField } from './stream-targets-field';
import { useCommitStreamTargets } from './use-commit-stream-targets';
import { expandableSwitchGroupLabel, group } from '../list-form.css';
import {
  emptySearchResult,
  headerSummary,
  streamsList,
} from './custom-targets-section.css';

const ListFormStreamTargetsQuery = gql(`
  query ListFormStreamTargetsQuery($id: ID!) {
    stream(id: $id) {
      id
      customTargets
      customTargetsData {
        ...CustomTargetsDataFragment
      }
      subscription {
        active
        cancelAt
        price
        customTargets
        interval
        intervalCount
        signupTrial
      }
    }
  }
`);

/** Targets a save added, for the lists pinned to a subset of that stream. */
export interface TargetAdditions {
  streamId: string;
  targets: string[];
}

/** Writes each stream's staged targets; the form runs it before it saves. */
export type CommitStreamTargets = () => Promise<{
  ok: boolean;
  additions: TargetAdditions[];
}>;

export const CustomTargetsSection = ({
  sources,
  value,
  onChange,
  matchingSourcesOnly,
  collectionId,
  commitRef,
}: {
  sources: Stream[];
  value: SourceCustomTargetsMap;
  onChange: (value: SourceCustomTargetsMap) => void;
  matchingSourcesOnly: boolean;
  collectionId?: string;
  commitRef: RefObject<CommitStreamTargets | null>;
}) => {
  const me = useMe();
  const customStreams = sources.filter((stream) =>
    isSubscribedCustomStream(stream, me.id),
  );

  // Read once: picking a stream later must not move a section the user set.
  const [openByDefault] = useState(
    matchingSourcesOnly && customStreams.length > 0,
  );
  const [open, setOpen] = useState(openByDefault);

  const commits = useRef(new Map<string, CommitStreamTargets>());
  commitRef.current = async () => {
    const results = await Promise.all(
      [...commits.current.values()].map((commit) => commit()),
    );
    return {
      ok: results.every((result) => result.ok),
      additions: results.flatMap((result) => result.additions),
    };
  };

  const setStreamTargets = (streamId: string, targets?: string[]) => {
    if (targets === undefined) {
      const { [streamId]: _removed, ...rest } = value;
      onChange(rest);
    } else {
      onChange({ ...value, [streamId]: targets });
    }
  };

  if (customStreams.length === 0) {
    return null;
  }

  const summary = `${customStreams.length} ${
    customStreams.length === 1 ? 'stream' : 'streams'
  }`;

  return (
    <div
      className={group}
      {...onboardingTargetProps(ONBOARDING_TARGETS.editListHighlight)}
    >
      <Collapsible
        open={open}
        onOpenChange={setOpen}
        data-testid="custom-targets"
      >
        <CollapsibleTrigger asChild>
          <div className={expandableSwitchGroupLabel}>
            Custom targets
            {!open && <span className={headerSummary}>{summary}</span>}
            {open ? (
              <MinusIcon width={20} height={20} />
            ) : (
              <PlusIcon width={20} height={20} />
            )}
          </div>
        </CollapsibleTrigger>
        {/* Mounted while closed: collapsing the section would otherwise
            drop every staged edit and its pending save. */}
        <CollapsibleContent forceMount>
          <div className={streamsList}>
            {customStreams.map((stream) => (
              <StreamTargets
                key={stream.id}
                stream={stream}
                collectionId={collectionId}
                commits={commits}
                explicitTargets={value[stream.id]}
                onExplicitTargetsChange={(targets) =>
                  setStreamTargets(stream.id, targets)
                }
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

const StreamTargets = ({
  stream,
  collectionId,
  commits,
  explicitTargets,
  onExplicitTargetsChange,
}: {
  stream: Stream;
  collectionId?: string;
  commits: RefObject<Map<string, CommitStreamTargets>>;
  explicitTargets?: string[];
  onExplicitTargetsChange: (targets: string[] | undefined) => void;
}) => {
  const { data, loading } = useQuery(ListFormStreamTargetsQuery, {
    variables: { id: stream.id },
  });
  const saved = data?.stream?.customTargets;
  const commit = useCommitStreamTargets();

  // undefined until edited: the stream's own targets stand in.
  const [staged, setStaged] = useState<string[] | undefined>(undefined);
  const [isBuyingTargets, setIsBuyingTargets] = useState(false);
  const allTargets = useMemo(() => staged ?? saved ?? [], [saved, staged]);

  // A target the stream lost cannot stay in this list's subset. Not before
  // the stream's own targets arrive, though: against an empty list every
  // target looks lost, and the subset would be wiped on the way in.
  useEffect(() => {
    if (!saved || !explicitTargets) return;
    const known = new Set(allTargets);
    const pruned = explicitTargets.filter((target) => known.has(target));
    if (pruned.length !== explicitTargets.length) {
      onExplicitTargetsChange(pruned);
    }
  }, [allTargets, explicitTargets, onExplicitTargetsChange, saved]);

  // A pinned subset does not pick new targets up on its own.
  const subsetLists = useSubsetLists(stream.id, collectionId);

  useEffect(() => {
    const registry = commits.current;
    const id = stream.id;
    registry.set(id, async () => {
      if (!staged) return { ok: true, additions: [] };

      const known = new Set(saved ?? []);
      const added = staged.filter((target) => !known.has(target));
      const ok = await commit(id, staged);

      return {
        ok,
        additions:
          ok && added.length > 0 && subsetLists.length > 0
            ? [{ streamId: id, targets: added }]
            : [],
      };
    });
    return () => {
      registry.delete(id);
    };
  }, [commit, commits, saved, staged, stream.id, subsetLists.length]);

  if (loading && !saved) {
    return (
      <div className={emptySearchResult}>
        <Spinner size={16} />
      </div>
    );
  }

  const customTargetsData = data?.stream?.customTargetsData;
  const subscription = data?.stream?.subscription;

  return (
    <>
      <StreamTargetsField
        streamId={stream.id}
        streamName={stream.name}
        allTargets={allTargets}
        onAllTargetsChange={setStaged}
        savedTargets={saved ?? []}
        explicitTargets={explicitTargets}
        onExplicitTargetsChange={onExplicitTargetsChange}
        quota={subscription?.customTargets ?? 0}
        onBuyMore={
          customTargetsData && subscription
            ? () => setIsBuyingTargets(true)
            : undefined
        }
        instruction={customTargetsData?.targetTypeInstruction}
        targetValidationRegex={customTargetsData?.targetValidationRegex}
      />
      {customTargetsData && subscription ? (
        <TargetsModal
          streamId={stream.id}
          open={isBuyingTargets}
          onOpenChange={setIsBuyingTargets}
          defaultView="updateQuantity"
          maxTargets={subscription.customTargets ?? 0}
          initialTargets={allTargets}
          customTargetsData={customTargetsData}
          subscription={subscription}
        />
      ) : null}
    </>
  );
};
